import { ImprovedNoise } from 'three/examples/jsm/math/ImprovedNoise'
// import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise'

export enum BiomeType {
  beach = 'beach',
  dunes = 'dunes',
  scrub = 'scrub',
  plains = 'plains',
  forest = 'forest',
  highlands = 'highlands',
  rockyRidges = 'rockyRidges',
  snow = 'snow',
  omori = 'omori'
}

export default class Noise {
  noise = new ImprovedNoise()
  seed = Math.random()
  gap = 22
  amp = 8
  seaLevel = 27
  terrainBase = 30
  terrainMin = 14
  terrainMax = 64
  domainWarpScale = 24

  // Memoization: getTerrainHeight costs ~30 Perlin evaluations (domain warp +
  // 5 fbm stacks) and is queried thousands of times per frame by collision,
  // highlighting, mobs, and drops — almost always for the same columns.
  // Caches self-invalidate when any seed changes (new game / world load).
  heightMemo = new Map<string, number>()
  treeMemo = new Map<string, boolean>()
  caveMemo = new Map<string, boolean>()
  // cave memo can grow large during worker chunk generation — only the main
  // thread (Terrain constructor) turns it on.
  caveMemoEnabled = false
  memoStamp = NaN
  static MEMO_MAX = 150000

  getMemoStamp = () => {
    return (
      this.seed +
      this.stoneSeed * 3 +
      this.treeSeed * 5 +
      this.coalSeed * 7 +
      this.leafSeed * 11
    )
  }

  syncMemo = () => {
    const stamp = this.getMemoStamp()
    if (stamp !== this.memoStamp) {
      this.memoStamp = stamp
      this.heightMemo.clear()
      this.treeMemo.clear()
      this.caveMemo.clear()
      this.foundationHeight = null
    }
  }

  stoneSeed = this.seed * 0.4
  stoneGap = 12
  stoneAmp = 8
  stoneThreshold = 7.25

  coalSeed = this.seed * 0.5
  coalGap = 3
  coalAmp = 8
  coalThreshold = 0.55

  treeSeed = this.seed * 0.7
  treeGap = 2
  treeAmp = 6
  treeHeight = 7
  treeThreshold = 0.78

  leafSeed = this.seed * 0.8
  leafGap = 2
  leafAmp = 5
  leafThreshold = -0.03

  get = (x: number, y: number, z: number) => {
    return this.noise.noise(x, y, z)
  }

  clamp = (value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value))
  }

  smoothstep = (edge0: number, edge1: number, value: number) => {
    const t = this.clamp((value - edge0) / (edge1 - edge0), 0, 1)
    return t * t * (3 - 2 * t)
  }

  positiveMod = (value: number, divisor: number) => {
    return ((value % divisor) + divisor) % divisor
  }

  hash2 = (x: number, z: number, seed: number) => {
    const n = Math.sin(x * 127.1 + z * 311.7 + seed * 74.7) * 43758.5453123
    return n - Math.floor(n)
  }

  fbm2 = (
    x: number,
    z: number,
    seed: number,
    octaves = 5,
    lacunarity = 2,
    gain = 0.5
  ) => {
    let value = 0
    let amplitude = 0.5
    let frequency = 1
    let normalizer = 0

    for (let octave = 0; octave < octaves; octave++) {
      value +=
        this.get(x * frequency, z * frequency, seed + octave * 19.191) *
        amplitude
      normalizer += amplitude
      amplitude *= gain
      frequency *= lacunarity
    }

    return normalizer === 0 ? 0 : value / normalizer
  }

  ridgedFbm2 = (
    x: number,
    z: number,
    seed: number,
    octaves = 4,
    lacunarity = 2,
    gain = 0.48
  ) => {
    let value = 0
    let amplitude = 0.5
    let frequency = 1
    let normalizer = 0

    for (let octave = 0; octave < octaves; octave++) {
      const n = this.get(x * frequency, z * frequency, seed + octave * 23.711)
      value += (1 - Math.abs(n)) * amplitude
      normalizer += amplitude
      amplitude *= gain
      frequency *= lacunarity
    }

    return normalizer === 0 ? 0 : value / normalizer
  }

  domainWarp = (x: number, z: number) => {
    const warpX =
      this.fbm2(x * 0.018 + 11.7, z * 0.018 - 3.1, this.seed + 13.37, 4) *
      this.domainWarpScale
    const warpZ =
      this.fbm2(x * 0.018 - 5.3, z * 0.018 + 17.9, this.seed + 31.73, 4) *
      this.domainWarpScale

    return { x: x + warpX, z: z + warpZ }
  }

  getTerrainHeight = (x: number, z: number) => {
    this.syncMemo()
    const key = `${x}_${z}`
    const memoized = this.heightMemo.get(key)
    if (memoized !== undefined) return memoized

    const height = this.computeTerrainHeight(x, z)
    if (this.heightMemo.size >= Noise.MEMO_MAX) this.heightMemo.clear()
    this.heightMemo.set(key, height)
    return height
  }

  foundationHeight: number | null = null
  getFoundationHeight = () => {
    if (this.foundationHeight === null) {
      this.foundationHeight = this.computeRawTerrainHeight(30, 0)
    }
    return this.foundationHeight
  }

  computeRawTerrainHeight = (x: number, z: number) => {
    const warped = this.domainWarp(x, z)
    const continent = this.fbm2(
      warped.x * 0.006,
      warped.z * 0.006,
      this.seed + 7.11,
      5,
      2,
      0.54
    )
    const hills = this.fbm2(
      warped.x * 0.026,
      warped.z * 0.026,
      this.seed + 23.41,
      5,
      2.05,
      0.48
    )
    const detail = this.fbm2(
      warped.x * 0.088,
      warped.z * 0.088,
      this.seed + 41.09,
      3,
      2.2,
      0.38
    )
    const ridges = this.ridgedFbm2(
      warped.x * 0.014,
      warped.z * 0.014,
      this.seed + 59.97,
      4
    )
    const erosion = this.fbm2(
      warped.x * 0.011,
      warped.z * 0.011,
      this.seed + 71.3,
      4
    )
    const mountainMask = this.smoothstep(
      0.08,
      0.68,
      continent + ridges * 0.24 - erosion * 0.1
    )

    let height =
      this.terrainBase +
      continent * 9 +
      hills * (4 + mountainMask * 5) +
      detail * 1.5 +
      ridges * mountainMask * 16

    // Flatten OMORI biome terrain to feel like the White Space void
    const temperature = this.getTemperature(x, z)
    const moisture = this.getMoisture(x, z)
    if (temperature < -0.25 && moisture < -0.15) {
      height = this.terrainBase + continent * 2 + detail * 0.3
    }

    return Math.floor(this.clamp(height, this.terrainMin, this.terrainMax))
  }

  computeTerrainHeight = (x: number, z: number) => {
    if (x >= 20 && x <= 39 && ((z >= -9 && z <= 9) || (z >= 15 && z <= 33))) {
      return this.getFoundationHeight() - 1
    }
    return this.computeRawTerrainHeight(x, z)
  }

  // NOTE: callers always pass the column's own terrain height, so memoizing
  // on (x, y, z) alone is sound.
  isCaveAt = (
    x: number,
    y: number,
    z: number,
    height = this.getTerrainHeight(x, z)
  ) => {
    const depth = height - y
    if (y < 5 || depth < 4) return false

    if (this.caveMemoEnabled) {
      this.syncMemo()
      const key = `${x}_${y}_${z}`
      const memoized = this.caveMemo.get(key)
      if (memoized !== undefined) return memoized

      const result = this.computeCaveAt(x, y, z, depth)
      if (this.caveMemo.size >= Noise.MEMO_MAX) this.caveMemo.clear()
      this.caveMemo.set(key, result)
      return result
    }

    return this.computeCaveAt(x, y, z, depth)
  }

  computeCaveAt = (x: number, y: number, z: number, depth: number) => {

    const warped = this.domainWarp(x + 137.3, z - 91.6)
    const depthMask = this.smoothstep(4, 18, depth)
    const tunnel = Math.abs(
      this.get(
        warped.x * 0.042,
        y * 0.074 + this.seed * 0.19,
        warped.z * 0.042
      )
    )
    const chamber = this.get(
      warped.x * 0.025 + 71.7,
      y * 0.041 - this.seed * 0.23,
      warped.z * 0.025 - 18.4
    )

    return (
      tunnel < 0.055 * depthMask ||
      (tunnel < 0.105 * depthMask && chamber > 0.47)
    )
  }

  isNetherCaveAt = (x: number, y: number, z: number) => {
    const n1 = this.get(x * 0.018, y * 0.035, z * 0.018)
    const n2 = this.get(x * 0.045 + 100, y * 0.07, z * 0.045 + 100)
    const isCave = n1 * 0.7 + n2 * 0.3 > -0.15
    if (y >= 60 || y <= 3) return false
    return isCave
  }

  hash3 = (x: number, y: number, z: number, seed: number) => {
    const n = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719 + seed) * 43758.5453123
    return n - Math.floor(n)
  }

  getStoneScore = (x: number, z: number, height = this.getTerrainHeight(x, z)) => {
    const warped = this.domainWarp(x + 71.3, z - 19.8)
    const cliffiness = Math.max(0, height - 36) * 0.08
    return (
      this.ridgedFbm2(
        warped.x * 0.044,
        warped.z * 0.044,
        this.stoneSeed + 101.9,
        4
      ) *
        this.stoneAmp +
      cliffiness
    )
  }

  getCoalScore = (x: number, y: number, z: number) => {
    return this.get(x * 0.18, y * 0.12, z * 0.18 + this.coalSeed)
  }

  getMoisture = (x: number, z: number) => {
    const warped = this.domainWarp(x - 33.2, z + 58.6)
    return this.fbm2(
      warped.x * 0.014,
      warped.z * 0.014,
      this.seed + 89.2,
      4,
      2,
      0.52
    )
  }

  getTemperature = (x: number, z: number) => {
    const warped = this.domainWarp(x + 18.4, z - 47.2)
    return this.fbm2(
      warped.x * 0.009,
      warped.z * 0.009,
      this.seed + 113.5,
      4,
      2,
      0.5
    )
  }

  getBiomeAt = (x: number, z: number, height = this.getTerrainHeight(x, z)) => {
    const temperature = this.getTemperature(x, z)
    const moisture = this.getMoisture(x, z)

    if (temperature < -0.25 && moisture < -0.15) {
      return BiomeType.omori
    }

    if (height <= this.seaLevel + 1) return BiomeType.beach

    const stoneScore = this.getStoneScore(x, z, height)

    if (stoneScore > this.stoneThreshold + 0.45 || height >= 41) {
      return BiomeType.rockyRidges
    }
    if (temperature < -0.15) {
      return BiomeType.snow
    }
    if (height >= 36) {
      return moisture > 0.18 ? BiomeType.forest : BiomeType.highlands
    }
    if (temperature > 0.28 && moisture < -0.22) return BiomeType.dunes
    if (temperature > 0.12 && moisture < -0.02) return BiomeType.scrub
    if (moisture > 0.24) return BiomeType.forest
    return BiomeType.plains
  }

  getTreeScore = (x: number, z: number, height = this.getTerrainHeight(x, z)) => {
    const moisture = this.getMoisture(x, z)
    const biome = this.getBiomeAt(x, z, height)
    const forestMask = this.smoothstep(-0.08, 0.42, moisture)
    const lowlandMask = 1 - this.smoothstep(36, 43, height)
    const biomeTreeBias =
      biome === BiomeType.forest
        ? 0.22
        : biome === BiomeType.plains
          ? -0.08
          : biome === BiomeType.highlands
            ? -0.18
            : biome === BiomeType.omori
              ? 0.3
              : -0.6
    const groveNoise = this.fbm2(
      x * 0.052,
      z * 0.052,
      this.treeSeed + 3.19,
      3,
      2.1,
      0.5
    )

    return forestMask * lowlandMask * 0.65 + groveNoise * 0.35 + biomeTreeBias
  }

  shouldGrowTreeAt = (x: number, z: number, height = this.getTerrainHeight(x, z)) => {
    this.syncMemo()
    const key = `${x}_${z}`
    const memoized = this.treeMemo.get(key)
    if (memoized !== undefined) return memoized

    const result = this.computeShouldGrowTreeAt(x, z, height)
    if (this.treeMemo.size >= Noise.MEMO_MAX) this.treeMemo.clear()
    this.treeMemo.set(key, result)
    return result
  }

  computeShouldGrowTreeAt = (
    x: number,
    z: number,
    height = this.getTerrainHeight(x, z)
  ) => {
    if (x >= 15 && x <= 44 && z >= -14 && z <= 38) {
      return false
    }
    const biome = this.getBiomeAt(x, z, height)
    if (
      biome === BiomeType.beach ||
      biome === BiomeType.dunes ||
      biome === BiomeType.scrub ||
      biome === BiomeType.rockyRidges
    ) {
      return false
    }
    if (this.getStoneScore(x, z, height) > this.stoneThreshold) return false

    const spacing =
      biome === BiomeType.forest ? 9 : biome === BiomeType.plains ? 14 : 18
    const cellX = Math.floor(x / spacing)
    const cellZ = Math.floor(z / spacing)
    const offsetX = Math.floor(
      this.hash2(cellX, cellZ, this.treeSeed + 11.13) * spacing
    )
    const offsetZ = Math.floor(
      this.hash2(cellX, cellZ, this.treeSeed + 29.71) * spacing
    )

    if (
      this.positiveMod(x, spacing) !== offsetX ||
      this.positiveMod(z, spacing) !== offsetZ
    ) {
      return false
    }

    const treeScore = this.getTreeScore(x, z, height)
    const chance = this.hash2(cellX, cellZ, this.treeSeed + 47.9)
    const chanceThreshold = biome === BiomeType.forest ? 0.32 : 0.62
    return treeScore > this.treeThreshold && chance > chanceThreshold
  }
}
